import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableBody,
  TableCell,
  TableRow,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { SearchVouchers_search_edges_node } from "@saleor/searches/types/SearchVouchers";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FormattedMessage, useIntl } from "react-intl";

import Checkbox from "../Checkbox";

export interface FormData {
  vouchers: SearchVouchers_search_edges_node[];
  query: string;
}

const useStyles = makeStyles(
  {
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    }
  },
  { name: "AssignVoucherDialog" }
);

export interface AssignVoucherDialogProps extends FetchMoreProps {
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  vouchers: SearchVouchers_search_edges_node[];
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: SearchVouchers_search_edges_node[]) => void;
}

function handleVoucherAssign(
  voucher: SearchVouchers_search_edges_node,
  isSelected: boolean,
  selectedVouchers: SearchVouchers_search_edges_node[],
  setSelectedVouchers: (data: SearchVouchers_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedVouchers(
      selectedVouchers.filter(
        selectedVoucher => selectedVoucher.id !== voucher.id
      )
    );
  } else {
    setSelectedVouchers([...selectedVouchers, voucher]);
  }
}

const scrollableTargetId = "assignVoucherScrollableDialog";

const AssignVoucherDialog: React.FC<AssignVoucherDialogProps> = props => {
  const {
    confirmButtonState,
    hasMore,
    open,
    loading,
    vouchers,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [selectedVouchers, setSelectedVouchers] = React.useState<
    SearchVouchers_search_edges_node[]
  >([]);

  const handleSubmit = () => onSubmit(selectedVouchers);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      classes={{ paper: scrollableDialogClasses.dialog }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <FormattedMessage
          defaultMessage="Assign Voucher"
          description="dialog header"
        />
      </DialogTitle>
      <DialogContent className={scrollableDialogClasses.topArea}>
        <TextField
          name="query"
          value={query}
          onChange={onQueryChange}
          label={intl.formatMessage({
            defaultMessage: "Search Vouchers"
          })}
          placeholder={intl.formatMessage({
            defaultMessage:
              "Search by voucher name, attribute, voucher type etc..."
          })}
          fullWidth
          InputProps={{
            autoComplete: "off",
            endAdornment: loading && <CircularProgress size={16} />
          }}
        />
      </DialogContent>
      <DialogContent
        className={scrollableDialogClasses.scrollArea}
        id={scrollableTargetId}
      >
        <InfiniteScroll
          dataLength={vouchers?.length}
          next={onFetchMore}
          hasMore={hasMore}
          scrollThreshold="100px"
          loader={
            <div className={scrollableDialogClasses.loadMoreLoaderContainer}>
              <CircularProgress size={16} />
            </div>
          }
          scrollableTarget={scrollableTargetId}
        >
          <ResponsiveTable key="table">
            <TableBody>
              {vouchers &&
                vouchers.map(voucher => {
                  const isSelected = selectedVouchers.some(
                    selectedVoucher => selectedVoucher.id === voucher.id
                  );

                  return (
                    <TableRow
                      key={voucher.id}
                      data-test-id="assign-voucher-table-row"
                    >
                      <TableCell className={classes.colName}>
                        {voucher.name}
                      </TableCell>
                      <TableCell className={classes.colName}>
                        {voucher.code}
                      </TableCell>
                      <TableCell
                        padding="checkbox"
                        className={classes.checkboxCell}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() =>
                            handleVoucherAssign(
                              voucher,
                              isSelected,
                              selectedVouchers,
                              setSelectedVouchers
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </ResponsiveTable>
        </InfiniteScroll>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage {...buttonMessages.back} />
        </Button>
        <ConfirmButton
          data-test="submit"
          transitionState={confirmButtonState}
          color="primary"
          variant="contained"
          type="submit"
          onClick={() => {
            setSelectedVouchers([]);
            handleSubmit();
          }}
        >
          <FormattedMessage
            defaultMessage="Assign vouchers"
            description="button"
          />
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};
AssignVoucherDialog.displayName = "AssignVoucherDialog";
export default AssignVoucherDialog;
